import { types, getSnapshot, type Instance, type SnapshotIn } from 'mobx-state-tree'
import { CenterLocationModel } from '../common/CenterLocationModel'
import { WithEditableLatLngModel } from '../common/WithLatLng'
import { IsEditingModel } from '../IsEditingModel'

// We call it "kind" cause "type" in TS is confusing.
export enum FlightNetworkNodeKind {
  Waypoint = 'waypoint',
  Delivery = 'delivery',
  Semaphore = 'semaphore',
  Launchpad = 'launchpad',
}

export enum DiffKind {
  UNCHANGED = 'unchanged',
  ADDED = 'added',
  MODIFIED = 'modified',
  DELETED = 'deleted'
}

export function flightNetworkNodeKindDisplayName(kind: FlightNetworkNodeKind): string {
  return {
    [FlightNetworkNodeKind.Delivery]: 'Delivery point',
    [FlightNetworkNodeKind.Waypoint]: 'Waypoint',
    [FlightNetworkNodeKind.Semaphore]: 'Semaphore',
    [FlightNetworkNodeKind.Launchpad]: 'Launchpad',
  }[kind]
}

export const BaseFlightNetworkNodeModel = WithEditableLatLngModel
  .named('BaseFlightNetworkNodeModel')
  .props({
    id: types.identifierNumber,

    name: types.string,
    flight_altitude_m: types.maybeNull(types.number), 
    ground_elevation_m: types.maybeNull(types.number),
    is_test_point: types.optional(types.boolean, false),
    is_safe_point: types.optional(types.boolean, false),
    node_type: types.enumeration('FlightNetworkNodeKind', [...Object.values(FlightNetworkNodeKind)]),

    network_id: types.number, // We could use `types.reference` here, but there's no need really, for now at least.
  })
  .views((self) => ({
    get groundElevationMeters(): typeof self.ground_elevation_m {
      return self.ground_elevation_m
    },
    get flightAltitudeMeters(): typeof self.flight_altitude_m {
      return self.flight_altitude_m
    },
    get nodeKind(): FlightNetworkNodeKind {
      return self.node_type
    },
    get isNameValid() : boolean {
      return self.name.length > 0
    },
    get isFlightAltitudeValid() : boolean {
      return self.flight_altitude_m !== null &&
        !isNaN(self.flight_altitude_m) &&
        self.flight_altitude_m > 0
    },
    get isGroundElevationValid() : boolean {
      return ![null, NaN].includes(self.ground_elevation_m)
    },
    get isLaunchpad() : boolean {
      return self.node_type === FlightNetworkNodeKind.Launchpad
    },
    get isSafePoint(): boolean {
      return self.is_safe_point
    },
    get canBeSafePoint(): boolean {
      return self.node_type === FlightNetworkNodeKind.Semaphore || self.node_type === FlightNetworkNodeKind.Waypoint
    },
  }))
  .volatile((self) => ({
    _isDeletedInDraft: false, // Marks nodes which exist in the operational network but deleted in the draft network
    _operationalNetworkNode: undefined as (typeof self | null | undefined), // For draft network nodes, a reference to the existing node in the current network, to track node status (added/changed/unchanged)
  }))
  .views((self) => ({
    get isValid() : boolean {
      return self.isNameValid &&
       self.isLatitudeValid &&
       self.isLongitudeValid &&
       self.isFlightAltitudeValid && 
       self.isGroundElevationValid
   },
   get diffFromOperationalNetwork(): DiffKind {
    if (self._operationalNetworkNode === undefined) {
      // if `_operationalNetworkNode` was never set, this is not a draft node
      return DiffKind.UNCHANGED
    }
    if (self._isDeletedInDraft) {
      return DiffKind.DELETED
    }
    if (self._operationalNetworkNode === null) {
      return DiffKind.ADDED
    }
    if (
      self.name !== self._operationalNetworkNode.name ||
      self.latitudeDeg !== self._operationalNetworkNode.latitudeDeg ||
      self.longitudeDeg !== self._operationalNetworkNode.longitudeDeg ||
      self.flightAltitudeMeters !== self._operationalNetworkNode.flightAltitudeMeters ||
      self.groundElevationMeters !== self._operationalNetworkNode.groundElevationMeters || 
      self.nodeKind !== self._operationalNetworkNode.nodeKind ||
      self.isSafePoint !== self._operationalNetworkNode.isSafePoint
      ) {
        return DiffKind.MODIFIED
      }
      return DiffKind.UNCHANGED
   },
  }))
  .actions((self) => ({
    setName(name: typeof self.name): void {
      self.name = name
    },
    setGroundElevationMeters(groundElevationMeters: typeof self.groundElevationMeters) {
      self.ground_elevation_m = groundElevationMeters
    },
    setFlightAltitudeMeters(flightAltitudeMeters: typeof self.flightAltitudeMeters) {
      self.flight_altitude_m = flightAltitudeMeters
    },
    setOperationalNetworkNode(node: typeof self | null) {
      self._operationalNetworkNode = node
    },
    markAsDeletedInDraft(isDeleted: boolean) {
      self._isDeletedInDraft = isDeleted
    },
    toggleIsSafePoint() {
      self.is_safe_point = !self.is_safe_point
    }
  }))

export const NewFlightNetworkNodeModel = BaseFlightNetworkNodeModel.named('NewFlightNetworkNodeModel')
export const ExistingFlightNetworkNodeModel = BaseFlightNetworkNodeModel.named('ExistingFlightNetworkNodeModel')
  .props({
    uuid: types.string,
  })
// Note: order is important!
// ExistingFlightNetworkNodeModel must come first and NewFlightNetworkNodeModel second.
// when deserializing a union type, mobx chooses the first type which fits the schema. So the more specific type comes first.
export const FlightNetworkNodeModel = types.union(ExistingFlightNetworkNodeModel, NewFlightNetworkNodeModel)

const BaseFlightNetworkEdgeModel = types.model('FlightNetworkEdgeModel', {
  id: types.identifierNumber,

  from_vertex_id: types.reference(FlightNetworkNodeModel),
  to_vertex_id: types.reference(FlightNetworkNodeModel),

  network_id: types.number, // We could use `types.reference` here, but there's no need really, for now at least.
})
.views((self) => ({
  get fromNode(): FlightNetworkNode {
    return self.from_vertex_id
  },
  get toNode(): FlightNetworkNode {
    return self.to_vertex_id
  },  
}))
.volatile((self) =>({
  _isDeletedInDraft: false, // Marks edges which exist in the operational network but deleted in the draft network
  _operationalNetworkEdge: undefined as (typeof self | null | undefined), // For draft network edges, a reference to the existing node in the current network, to track edge status (added/changed/unchanged)
}))
.actions((self) => ({
  setOperationalNetworkEdge(edge: typeof self | null) {
    self._operationalNetworkEdge = edge
  },
  markAsDeletedInDraft(isDeleted: boolean) {
    self._isDeletedInDraft = isDeleted
  }
}))
.views((self) => ({
  get nodeUuidsKey(): string | null {
    if (!ExistingFlightNetworkNodeModel.is(self.fromNode) || !ExistingFlightNetworkNodeModel.is(self.toNode)) {
      return null
    }
    const [lowerUuid, higherUuid] = [self.fromNode.uuid, self.toNode.uuid].sort()
    return `${lowerUuid}-${higherUuid}`
  },
  get diffFromOperationalNetwork(): DiffKind {
    if (self._operationalNetworkEdge === undefined) {
      return DiffKind.UNCHANGED // Edge where `_operationalNetworkEdge` wasn't set is not from a draft network.
    }
    if (self._isDeletedInDraft) {
      return DiffKind.DELETED
    }
    if (self._operationalNetworkEdge === null) {
      return DiffKind.ADDED
    }
    if (self.fromNode.latitudeDeg !== self._operationalNetworkEdge.fromNode.latitudeDeg ||
      self.fromNode.longitudeDeg !== self._operationalNetworkEdge.fromNode.longitudeDeg ||
      self.fromNode.groundElevationMeters !== self._operationalNetworkEdge.fromNode.groundElevationMeters ||
      self.fromNode.flightAltitudeMeters !== self._operationalNetworkEdge.fromNode.flightAltitudeMeters ||

      self.toNode.latitudeDeg !== self._operationalNetworkEdge.toNode.latitudeDeg ||
      self.toNode.longitudeDeg !== self._operationalNetworkEdge.toNode.longitudeDeg ||
      self.toNode.groundElevationMeters !== self._operationalNetworkEdge.toNode.groundElevationMeters ||
      self.toNode.flightAltitudeMeters !== self._operationalNetworkEdge.toNode.flightAltitudeMeters) {
        return DiffKind.MODIFIED
    }
    return DiffKind.UNCHANGED
  }
}))

export const NewFlightNetworkEdgeModel = BaseFlightNetworkEdgeModel.named('NewFlightNetworkEdgeModel')
export const ExistingFlightNetworkEdgeModel = BaseFlightNetworkEdgeModel.named('ExistingFlightNetworkEdgeModel')

// Note: order is important!
// ExistingFlightNetworkEdgeModel must come before NewFlightNetworkEdgeModel, see previous similar comment
export const FlightNetworkEdgeModel = types.union(ExistingFlightNetworkEdgeModel, NewFlightNetworkEdgeModel)

export const FlightNetworkModel = IsEditingModel
  .named('FlightNetworkModel')
  .props({
    id: types.identifierNumber,
    name: types.string,
    center_locations: types.array(CenterLocationModel),
    edges: types.array(FlightNetworkEdgeModel),
    nodes: types.array(FlightNetworkNodeModel),
  })
  .volatile(() => ({
    // A set containing a unique key for every edge (in the form of `lowerId-higherId`) in the network.
    // We hold and maintain this map as an optimization for the add/delete edge use case, 
    // in order not to go over the entire edge list when checking whether a pair of vertices are already connected.
    _edgesByNodeIds: new Map<string, FlightNetworkEdge>(),

    // A mapping from node to its adjacent edges.
    // We hold and maintain this set as an optimization for the delete node use case, to avoid traversing all edges to remove the adjacent ones.
    _nodeToEdgeMapping: new Map<number, Set<FlightNetworkEdge>>(),

    _getEdgeMapKey: (from_vertex: FlightNetworkNode, to_vertex: FlightNetworkNode): string => {
      const lowerId = Math.min(from_vertex.id, to_vertex.id)
      const higherId =  Math.max(from_vertex.id, to_vertex.id)
      return `${lowerId}-${higherId}`
    }
  }))
  .views((self) => ({
    _getEdgeBetweenNodes(from_vertex: FlightNetworkNode, to_vertex: FlightNetworkNode): FlightNetworkEdge | null {
      const keyMap = self._getEdgeMapKey(from_vertex, to_vertex)
      return self._edgesByNodeIds.get(keyMap) ?? null
    },
    _getAdjacentEdges(node: FlightNetworkNode): Set<FlightNetworkEdge> {
      return self._nodeToEdgeMapping.get(node.id) ?? new Set()
    },
  }))
  .actions((self) => ({
    _addEdgeToDictionaries(edge: FlightNetworkEdge)  {
      const keyMap = self._getEdgeMapKey(edge.from_vertex_id, edge.to_vertex_id)
      self._edgesByNodeIds.set(keyMap, edge)

      if (!self._nodeToEdgeMapping.has(edge.fromNode.id)) {
        self._nodeToEdgeMapping.set(edge.fromNode.id, new Set())
      }
      self._nodeToEdgeMapping.get(edge.fromNode.id)?.add(edge)

      if (!self._nodeToEdgeMapping.has(edge.toNode.id)) {
        self._nodeToEdgeMapping.set(edge.toNode.id, new Set())
      }
      self._nodeToEdgeMapping.get(edge.toNode.id)?.add(edge)
    },
  }))
  .actions((self) => {
    function afterCreate() {
      self.edges.forEach(edge => self._addEdgeToDictionaries(edge))
    }
    function beforeDestroy() {
      self._edgesByNodeIds.clear()
      self._nodeToEdgeMapping.clear()
    }
    return {
      afterCreate,
      beforeDestroy
    }
  })
  .views((self) => ({
    get centerLocation(): Instance<typeof CenterLocationModel> {
      if (self.center_locations[0] === undefined) {
        throw new Error('Flight network must have center locations')
      }
      return self.center_locations[0] // We only support one center per Flight network.
    },
    get getMapBounds(): google.maps.LatLngBounds{
      const mapBounds = new google.maps.LatLngBounds()
      self.nodes.forEach((node) => { 
        const nodeLatLng = node.toGmapLatLng
        if (nodeLatLng !== null) {
          mapBounds.extend(nodeLatLng) 
        }
      })
      return mapBounds
    },
    // I could't override `isDirty` in the manner described in https://mobx-state-tree.js.org/tips/inheritance since `isDirty` is a getter and not a function.
    // This isn't super elegant, but it's simple and it works.
    get isFlightNetworkDirty() {
      return self.isDirty || 
      // if node/edge was deleted from network (but not a zombie node/edge, where the deletion was saved), then the network is also dirty, although the snapshot isn't changed 
      [...self.nodes, ...self.edges].some(element => element.diffFromOperationalNetwork === DiffKind.DELETED && element.id > 0)
    },
  }))
  .actions((self) => ({
    addNode(kind: FlightNetworkNodeKind, lat: number, lon: number): FlightNetworkNode {
      const maxId = (self.nodes.length <= 0) ? 0 :
        Math.max(...self.nodes.map(node => node.id))
      const newNode = NewFlightNetworkNodeModel.create({ 
        id: maxId + 1,
        name: '', 
        latitude_deg: lat.toString(), 
        longitude_deg: lon.toString(),
        flight_altitude_m: null, 
        ground_elevation_m: null,
        node_type: kind,
        network_id: self.id
      })
      newNode.setOperationalNetworkNode(null) // New nodes have no parallel in the operational network
      self.nodes.push(newNode)
      
      return newNode
    },
    addEdge(from: FlightNetworkNode, to: FlightNetworkNode): boolean {
      if (from.id === to.id) {
        return false
      }

      if (from.diffFromOperationalNetwork === DiffKind.DELETED || 
        to.diffFromOperationalNetwork === DiffKind.DELETED) {
          return false // TODO: error message?
      }

      const existingEdge = self._getEdgeBetweenNodes(from, to)
      if (existingEdge !== null) {
        if (existingEdge.diffFromOperationalNetwork === DiffKind.DELETED) {
          // Edge re-Added, revive it 🧟
          existingEdge.markAsDeletedInDraft(false)
          return true
        } else {
          return false
        } 
      }

      const maxId = (self.edges.length <= 0) ? 0 : 
        Math.max(...self.edges.map(edge => edge.id))
      const newEdge = NewFlightNetworkEdgeModel.create({
        id: maxId + 1,
        from_vertex_id: from.id, 
        to_vertex_id: to.id, 
        network_id: self.id
      })
      newEdge.setOperationalNetworkEdge(null)
      self.edges.push(newEdge)
      self._addEdgeToDictionaries(newEdge)
      return true
    },
    deleteEdge(edge: FlightNetworkEdge) {
      edge.markAsDeletedInDraft(true)
    },
  }))
  .actions((self) => ({
    deleteNode(node: FlightNetworkNode) {
      const adjacentEdges = self._getAdjacentEdges(node)
      adjacentEdges.forEach(edge => self.deleteEdge(edge))

      node.markAsDeletedInDraft(true)
    },
    /**
     * Calculate the diff of this (draft) flight network from a given (operational network).
     * This is for showing the node and edg colors in the table and map.
     * 
     * Note that for nodes & edges which appear in the operational network but not in the draft, we actually add a "zombie" node/edge with status DELETED, to be displayed in the table/map.
     */
    calculateDiffsFromOperationalNetwork(operationalNetwork: typeof self) {
      
      const nodesByUuid: {[key:string]: ExistingFlightNetworkNode} = self.nodes
        .filter((node): node is ExistingFlightNetworkNode => ExistingFlightNetworkNodeModel.is(node))
        .reduce((map, node) => ( map[node.uuid] = node, map ), {})
      const otherNetworkNodesByUuid: {[key:string]: ExistingFlightNetworkNode} = operationalNetwork.nodes
        .filter((node): node is ExistingFlightNetworkNode => ExistingFlightNetworkNodeModel.is(node))
        .reduce((map, node) => ( map[node.uuid] = node, map ), {})

      
      const edgesByNodeUuids = 
        Object.fromEntries(self.edges.filter(e => e.nodeUuidsKey !== null).map(e => [e.nodeUuidsKey, e])) as {[key:string]: FlightNetworkEdge}
      const otherNetworkEdgesByNodeUuids = 
        Object.fromEntries(operationalNetwork.edges.filter(e => e.nodeUuidsKey !== null).map(e => [e.nodeUuidsKey, e])) as {[key:string]: FlightNetworkEdge}

      self.nodes.forEach(node => {
        if (ExistingFlightNetworkNodeModel.is(node)) {
          const existingNode = otherNetworkNodesByUuid[node.uuid]
          node.setOperationalNetworkNode(existingNode ?? null)
        } else {
          node.setOperationalNetworkNode(null)          
        }
      })

      operationalNetwork.nodes.filter(node => ExistingFlightNetworkNodeModel.is(node) && !(node.uuid in nodesByUuid)).forEach(deletedNode => {
        // For all the nodes which exist in operational network but not in draft, we add to the draft a "zombie" node 🧟, to show in the table and map
        const deletedNodeSnapshot = getSnapshot(deletedNode)
        const cloneOfNodeForDraftNetwork = FlightNetworkNodeModel.create({
          ...deletedNodeSnapshot,
          id: deletedNode.id * -1 // Node objects must have unique id in mobx-state-tree. For the zombie node we use a negative id
        })
        cloneOfNodeForDraftNetwork.markAsDeletedInDraft(true)
        cloneOfNodeForDraftNetwork.setOperationalNetworkNode(deletedNode)
        self.nodes.push(cloneOfNodeForDraftNetwork)
      })

      self.edges.forEach(edge => {
        const key = edge.nodeUuidsKey
        if (key === null) {
          edge.setOperationalNetworkEdge(null)
        } else {
          const existingEdge = otherNetworkEdgesByNodeUuids[key]
          edge.setOperationalNetworkEdge(existingEdge ?? null)
        }
      })
      
      operationalNetwork.edges.filter(edge => edge.nodeUuidsKey !== null && !(edge.nodeUuidsKey in edgesByNodeUuids)).forEach(deletedEdge => {
        const edgeSnapshot = getSnapshot(deletedEdge)
        const cloneOfEdgeForDraftNetwork = FlightNetworkEdgeModel.create({
          ...edgeSnapshot, 
          id: deletedEdge.id * -1
        })
        cloneOfEdgeForDraftNetwork.markAsDeletedInDraft(true)
        cloneOfEdgeForDraftNetwork.setOperationalNetworkEdge(deletedEdge)
        
        self.edges.push(cloneOfEdgeForDraftNetwork)  
        self._addEdgeToDictionaries(cloneOfEdgeForDraftNetwork)
      })
    },
  }))

type FlightNetworkModelType = typeof FlightNetworkModel
export interface FlightNetworkModelInstance extends Instance<FlightNetworkModelType> {}
export interface FlightNetworkModelSnapshotIn extends SnapshotIn<FlightNetworkModelType> {}

type ExistingFlightNetworkNode = Instance<typeof ExistingFlightNetworkNodeModel>

export type FlightNetwork = Instance<typeof FlightNetworkModel>
export type FlightNetworkNode = Instance<typeof FlightNetworkNodeModel>
export type FlightNetworkEdge = Instance<typeof FlightNetworkEdgeModel>
